import React, { useEffect, useState } from "react";
import brandAPI from "../../api/brandApi";
import { showError, showSuccess } from "../../utils/toastUtils";
import { useLoader } from "../../utils/LoaderUtils";
import slugify from "slugify";
import { useParams } from "react-router-dom";
import SEOFields from "../../components/partials/SEOFields";
import ImageUploader from "../../components/partials/ImageUploader";
import { handleApiValidationError } from "../../utils/apiErrorHandler";
import SubmitButton from "../../components/partials/global/SubmitButton";


function Edit() {
  const params = useParams();
  const id = params.id;

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    slug: "",
    image: "",
    navbar: false,
    homepage: false,
  });

  const [seoData, setSeoData] = useState({
    metaTitle: "",
    metaDescription: "",
    tags: [],
  });

  const { showLoader, hideLoader } = useLoader();
  const [isLoading, setIsLoading] = useState(false);
  const [originalData, setOriginalData] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (id) fetchSingleDetails();
  }, [id]);

  const fetchSingleDetails = async () => {
    try {
      const res = await brandAPI.getSingleDetails(id);

      if (res.code === 200) {
        const data = res.data;

        setFormData({
          image: data.image || "",
          title: data.title || "",
          description: data.description || "",
          slug: data.slug || "",
          homepage: data.showOnHomepage || false,
          navbar: data.showInNavbar || false,
        });

        setOriginalData({
          image: data.image || "",
          title: data.title || "",
          description: data.description || "",
          slug: data.slug || "",
          homepage: data.showOnHomepage || false,
          navbar: data.showInNavbar || false,
        });

        setSeoData({
          metaDescription: data?.meta?.description || "",
          metaTitle: data?.meta?.title || "",
          tags: data?.meta?.keywords.split(",").map((tag) => tag.trim()) || [],
        });
      } else {
        showError(res.message);
      }
    } catch (err) {
      showError(err.message);
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = "Title field is mandatory";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleImageUploadSuccess = (type, url) => {
    setFormData((prev) => ({
      ...prev,
      [type]: url,
    }));
  };

  const getChangedFields = (original, current) => {
    const changed = {};

    Object.keys(current).forEach((key) => {
      if (current[key] !== original[key]) {
        // empty / null image ignore
        if (key === "image" && !current[key]) return;
        changed[key] = current[key];
      }
    });

    return changed;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsLoading(true);
    showLoader();

    if (!validate()) {
      setIsLoading(false);
      hideLoader();
      return;
    }

    try {
      const fullPayload = {
        ...formData,
        metaTitle: seoData.metaTitle,
        metaDescription: seoData.metaDescription,
        keywords: seoData.tags.join(","),
      };

      const payload = getChangedFields(originalData, fullPayload);

      if (Object.keys(payload).length === 0) {
        showError("No changes detected");
        setIsLoading(false);
        hideLoader();
        return;
      }

      const res = await brandAPI.updateDetails(id, payload);

      if (res.code === 200) {
        showSuccess(res.message);
      } else {
        showError(res.message);
      }
    } catch (err) {
      handleApiValidationError(err, setErrors);
    } finally {
      setIsLoading(false);
      hideLoader();
    }
  };

  return (
    <>
      <div className="content">
        <div className="page-header">
          <div className="page-title">
            <h4>Edit Brand Details</h4>
            <h6>
              Update brand details to showcase product listing on your store.
            </h6>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h4>Update Details</h4>
          </div>

          <div className="card-body profile-body">
            <form method="post" onSubmit={handleSubmit}>
              <ImageUploader
                type="image"
                label="Brand Image"
                value={formData.image}
                onUploadSuccess={handleImageUploadSuccess}
                assetType="brands"
              />

              <div className="row mt-3">
                <div className="col-md-6">
                  <label className="form-label">
                    Title <span className="text-danger">*</span>
                  </label>
                  <div className="input-group">
                    <span className="input-group-text" id="basic-addon1">
                      <i className="fa fa-pencil"></i>
                    </span>
                    <input
                      type="text"
                      name="title"
                      className="form-control"
                      placeholder="Title"
                      value={formData.title}
                      onChange={handleChange}
                    />
                  </div>
                  {errors.title && (
                    <span className="errors text-danger">{errors.title}</span>
                  )}
                </div>

                <div className="col-lg-3 col-sm-6">
                  <label className="form-label">
                    Show In Navbar<span className="text-danger">*</span>
                  </label>

                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      name="navbar"
                      id="flexSwitchCheckChecked"
                      checked={formData.navbar}
                      onChange={handleChange}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="flexSwitchCheckChecked"
                    >
                      Show
                    </label>
                  </div>
                </div>
                <div className="col-lg-3 col-sm-6">
                  <label className="form-label">
                    Show in Home Page<span className="text-danger">*</span>
                  </label>

                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      name="homepage"
                      id="flexSwitchCheckChecked"
                      checked={formData.homepage}
                      onChange={handleChange}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="flexSwitchCheckChecked"
                    >
                      Show
                    </label>
                  </div>
                </div>
              </div>

              <div className="row mt-3">
                <div className="col-md-12">
                  <label className="form-label">Description</label>
                  <textarea
                    name="description"
                    className="form-control"
                    value={formData.description}
                    onChange={handleChange}
                  ></textarea>
                </div>
              </div>

              <hr />

              <SEOFields
                seoData={seoData}
                setSeoData={setSeoData}
                slug={slugify(formData.title || "", { lower: true })}
                errors={errors}
              />

              <div className="row mt-3">
                <div className="col-12 d-flex justify-content-end">
                  <SubmitButton
                    isLoading={isLoading}
                    text="Submit Details"
                    loadingText="Submitting..."
                  ></SubmitButton>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}

export default Edit;
